<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="mt-3 border-bottom"
    >
      <BCol
        cols="12"
        md="6"
        offset-md="6"
      >
        <ValidationObserver
          ref="formSynchronizer"
        >
          <div class="d-flex flex-column flex-md-row gap-2 justify-content-end">

            <ValidationProvider
              v-slot="{ errors }"
              vid="start_date"
              name="start date"
              rules="required"
            >
              <BFormGroup
                label="Start Date"
                label-for="synchronizeStartDate"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="synchronizeStartDate"
                  v-model="synchronize.start_date"
                  :state="(errors.length > 0 ? false : null)"
                  :disabled="state.synchronizing"
                  :max="synchronize.end_date"
                  debounce="1000"
                  type="date"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>

            <ValidationProvider
              v-slot="{ errors }"
              vid="end_date"
              name="end date"
              rules="required"
            >
              <BFormGroup
                label="End Date"
                label-for="synchronizeEndDate"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="synchronizeEndDate"
                  v-model="synchronize.end_date"
                  :state="(errors.length > 0 ? false : null)"
                  :disabled="state.synchronizing"
                  :min="synchronize.start_date"
                  debounce="1000"
                  type="date"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>

            <div class="d-flex flex-column">
              <BFormGroup
                class="mb-2"
              >
                <label
                  for="actionButtons"
                  class="d-none d-md-block"
                >
                  &nbsp;
                </label>
                <div class="d-flex gap-2">
                  <BOverlay
                    :show="state.synchronizing"
                    rounded="sm"
                    opacity="0.6"
                    spinner-small
                    spinner-variant="primary"
                  >
                    <BButton
                      :variant="`${state.synchronizing ? 'outline-dark' : 'primary'}`"
                      :disabled="(state.importing || state.synchronizing)"
                      block
                      type="button"
                      @click.prevent="synchronizeCustomers"
                    >
                      Sync
                    </BButton>
                  </BOverlay>
                  <BOverlay
                    :show="state.importing"
                    rounded="sm"
                    opacity="0.6"
                    spinner-small
                    spinner-variant="secondary"
                  >
                    <BButton
                      :variant="`${state.importing ? 'outline-dark' : 'secondary'}`"
                      :disabled="(state.importing || state.synchronizing)"
                      block
                      type="button"
                      @click.prevent="importCustomers"
                    >
                      Import
                    </BButton>
                  </BOverlay>
                </div>
              </BFormGroup>
            </div>
          </div>
        </ValidationObserver>
      </BCol>
    </BRow>

    <BRow
      class="mt-3"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterCustomerSearch"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterCustomerSearch"
            v-model="tableCustomers.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableCustomers"
      :provider="tableProvider"
      :fields="tableCustomers.fields"
      :filter="tableCustomers.filter"
      :filter-on="tableCustomers.filterOn"
      :sort-by="tableCustomers.sortBy"
      :sort-desc="tableCustomers.sortDesc"
      :sort-direction="tableCustomers.sortDirection"
      :total-rows="tableCustomers.totalRows"
      :current-page="tableCustomers.currentPage"
      :per-page="tableCustomers.perPage"
      :stacked="tableCustomers.stacked"
      :busy="tableCustomers.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableCustomers.busy"
            @click.prevent="editCustomer(item)"
          >
            Edit
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalCustomer"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      title="Edit Customer"
    >
      <ValidationObserver
        ref="formCustomer"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="customer_code"
                name="customer code"
                rules="required"
              >
                <BFormGroup
                  label="Customer Code"
                  label-for="fCustomerCode"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fCustomerCode"
                    v-model="formCustomer.customer_code"
                    placeholder="enter customer code here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy || isVerified"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="8"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="customer_name"
                name="customer name"
                rules="required"
              >
                <BFormGroup
                  label="Customer Name"
                  label-for="fCustomerName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fCustomerName"
                    v-model="formCustomer.customer_name"
                    placeholder="enter customer name here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy || isVerified"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              class="py-2"
            >
              <div class="border-bottom" />
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="contact_person"
                name="contact person"
                rules="required"
              >
                <BFormGroup
                  label="Contact Person"
                  label-for="fContactPerson"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fContactPerson"
                    v-model="formCustomer.contact_person"
                    placeholder="enter contact person here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy || isVerified"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="mobile_number"
                name="mobile number"
                rules="min:11|max:11|regex:[0]{1}[9]{1}[0-9]{8}"
              >
                <BFormGroup
                  label="Mobile Number"
                  label-for="fMobileNumber"
                  label-class="font-weight-bolder"
                  description="Only if customer has network coverage"
                  class="mb-2"
                >
                  <BFormInput
                    id="fMobileNumber"
                    v-model="formCustomer.mobile_number"
                    v-mask="'0##########'"
                    placeholder="enter mobile number here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy || isVerified"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="8"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="email"
                name="email"
                rules="email"
              >
                <BFormGroup
                  label="Email"
                  label-for="fEmail"
                  label-class="font-weight-bolder"
                  description="Only if customer has an email."
                  class="mb-2"
                >
                  <BFormInput
                    id="fEmail"
                    v-model="formCustomer.email"
                    placeholder="enter email here"
                    type="email"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy || isVerified"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="address"
                name="address"
                rules="max:1000"
              >
                <BFormGroup
                  label="Landmark / Block No. / Street / Zone"
                  label-for="fAddress"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <template #label>
                    <span class="d-flex justify-content-between">
                      Landmark / Street / Zone
                      <span>
                        {{ formCustomer.address ? formCustomer.address.length : 0 }} / 1000
                      </span>
                    </span>
                  </template>
                  <BFormTextarea
                    id="fAddress"
                    v-model="formCustomer.address"
                    rows="5"
                    max-rows="12"
                    maxlength="1000"
                    placeholder="enter landmark / street / zone here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy || isVerified"
                    @keydown.enter="preventEnterKey"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="remarks"
                name="remarks"
                rules="max:1000"
              >
                <BFormGroup
                  label="Other Informations"
                  label-for="fRemarks"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <template #label>
                    <span class="d-flex justify-content-between">
                      Other Informations
                      <span>
                        {{ formCustomer.remarks ? formCustomer.remarks.length : 0 }} / 1000
                      </span>
                    </span>
                  </template>
                  <BFormTextarea
                    id="fRemarks"
                    v-model="formCustomer.remarks"
                    rows="5"
                    max-rows="12"
                    maxlength="1000"
                    placeholder="enter other informations here"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy || isVerified"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateCustomer"
        >
          Update Customer
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import { SAdminCustomer } from '@/services/admin'

export default {
  name: 'AdminCustomers',

  mixins: [
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Customers'
  }),

  data () {
    return {
      state: {
        busy: false,
        synchronizing: false,
        importing: false
      },
      synchronize: {
        start_date: '',
        end_date: ''
      },
      formCustomer: {
        id: '',
        customer_code: '',
        customer_name: '',
        contact_person: '',
        email: '',
        mobile_number: '',
        address: '',
        remarks: '',
        active: 1
      },
      tableCustomers: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 7, key: 'action', class: 'text-center' },
          { mobile: 1, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'customer_code', class: 'min-w-175px' },
          { mobile: 3, key: 'customer_name', class: 'min-w-175px' },
          { mobile: 4, key: 'active', class: 'text-center', formatter: this.yesOrNo },
          { mobile: 5, key: 'valid', class: 'text-center' },
          { mobile: 6, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableCustomers.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search
      }

      return await SAdminCustomer.get(payload).then(
        ({ data }) => {
          this.tableCustomers.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableCustomers.totalRows = 0
        return []
      }).finally(() => {
        this.tableCustomers.busy = false
      })
    },

    async synchronizeCustomers () {
      await this.$refs.formSynchronizer.validate().then(
        async validSynchronizer => {
          if (!validSynchronizer) {
            return this.swalInvalid()
          }

          this.state.synchronizing = true

          return SAdminCustomer.sync(this.synchronize).then(
            ({ data: { message } }) => {
              this.swalSuccess(message).then(() => {
                this.synchronize.start_date = null
                this.synchronize.end_date = null
                this.$refs.formSynchronizer.reset()
                this.$refs.tableCustomers.refetch()
              })
            }
          ).finally(() => {
            this.state.synchronizing = false
          })
        }
      )
    },

    async importCustomers () {
      this.state.importing = true
      return SAdminCustomer.import().then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.tableCustomers.refetch()
          })
        }
      ).finally(() => {
        this.state.importing = false
      })
    },

    editCustomer (customer) {
      this.formCustomer = Object.assign(this.formCustomer, {
        id: customer.id,
        customer_code: customer.customer_code,
        customer_name: customer.customer_name,
        contact_person: customer.contact_person,
        email: customer.email,
        mobile_number: customer.mobile_number,
        address: customer.address,
        remarks: customer.remarks,
        active: customer.active
      })

      this.$refs.modalCustomer.show()
    },

    async validateCustomer () {
      await this.$refs.formCustomer.validate().then(
        async validCustomer => {
          if (!validCustomer) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: '<h6>Update this Customer?</h6>',
            confirmButtonText: 'Update',
            preConfirm: () => {
              this.state.busy = true

              return this.putCustomer().catch(
                ({ message }) => {
                  this.$refs.formCustomer.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async putCustomer () {
      return await SAdminCustomer.put(this.formCustomer).then(
        ({ data: { message, product } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalCustomer.hide()

            this.$refs.tableCustomers.updateRow({
              id: product.id
            })
          })
        }
      )
    }
  }
}
</script>
